import React, { useEffect, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Tag, ModalShell } from "@/components/kit";

// Resolves short-lived signed URLs for private booking-attachments and renders
// image attachments as a thumbnail gallery (others as file chips). Clicking a
// photo opens it full-screen in an in-app lightbox (portaled via ModalShell).
export function PhotoGallery({ attachments = [], testId = "photo-gallery", emptyText = "None uploaded." }) {
  const [urls, setUrls] = useState({});
  const [active, setActive] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        attachments.map(async (a) => {
          const { data } = await supabase.storage.from("booking-attachments").createSignedUrl(a.storage_path, 3600);
          return [a.id, data?.signedUrl || null];
        }),
      );
      if (alive) setUrls(Object.fromEntries(entries));
    })();
    return () => { alive = false; };
  }, [attachments]);

  if (!attachments.length) return <p className="text-sm text-muted-foreground mt-2">{emptyText}</p>;
  const isImg = (a) => (a.file_type || "").startsWith("image") || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(a.file_name || "");

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-3" data-testid={testId}>
        {attachments.map((a) => {
          const url = urls[a.id];
          if (isImg(a)) {
            return (
              <button key={a.id} type="button" data-testid={`gallery-photo-${a.id}`} onClick={() => url && setActive({ ...a, url })}
                className="group relative block h-24 w-24 rounded-[8px] overflow-hidden border border-border hover:border-primary transition-colors">
                {url
                  ? <img src={url} alt={a.file_name || "evidence"} className="h-full w-full object-cover" loading="lazy" />
                  : <span className="grid place-items-center h-full w-full text-muted-foreground"><Paperclip className="h-4 w-4" /></span>}
                {a.visibility && <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate">{a.visibility}</span>}
              </button>
            );
          }
          return (
            <a key={a.id} href={url || undefined} target="_blank" rel="noreferrer" data-testid={`gallery-file-${a.id}`}
              className="text-sm border border-border rounded-[6px] px-3 py-2 hover:bg-secondary inline-flex items-center gap-1">
              <Paperclip className="h-3.5 w-3.5" /> {a.file_name || "attachment"} <Tag tone="muted" className="ml-1">{a.visibility}</Tag>
            </a>
          );
        })}
      </div>

      {active && (
        <ModalShell onClose={() => setActive(null)} className="max-w-3xl bg-transparent border-0 shadow-none" testId="gallery-lightbox">
          <div className="relative">
            <button data-testid="gallery-lightbox-close" onClick={() => setActive(null)} aria-label="Close"
              className="absolute -top-3 -right-3 z-10 rounded-full bg-card border border-border p-1.5 shadow hover:bg-secondary">
              <X className="h-4 w-4" />
            </button>
            <img src={active.url} alt={active.file_name || "evidence"} className="w-full max-h-[80vh] object-contain rounded-[8px] bg-black/20" />
            <p className="mt-2 text-center text-xs text-white/90">{active.file_name} · {active.visibility}</p>
          </div>
        </ModalShell>
      )}
    </>
  );
}
